import React from "react";

import InsurancePolicyCard from "../coverage/InsurancePolicyCard";
import RiskFinancingCurve from "../shared/RiskFinancingCurve";

import ScenarioSummaryCard from "./ScenarioSummaryCard";
//import CurrentCoverageCard from "./CurrentCoverageCard";
import CurrentCoveragesPanel from "../coverage/CurrentCoveragesPanel";
import ScenarioFinancialPosition from "./ScenarioFinancialPosition";

import {
  formatMoney,
  formatRatio,
} from "../../../utils/formatters";

export default function ScenarioFinancingAnalysis({
  scenarios,
  selectedScenarioId,
  strategy,
  data,
  settings,
  scenariosLoading,
  analysisLoading,
  scenariosError,
  analysisError,
  onScenarioChange,
  onStrategyChange,
  onAnalyze,
  onCompareAlternatives,
}) {
  const currency = settings?.currency || "EUR";

  const summary = data?.summary || {};

  const scenario = data?.scenario || null;
  const baseline = data?.baseline || {};
  const currentCoverages =
    Array.isArray(data?.current_coverages)
      ? data.current_coverages
      : [];
  
  const financialPosition =
    data?.financial_position || {};

  const scrollToSection = (elementId) => {
    const element =
      document.getElementById(elementId);

    if (!element) return;

    element.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleReviewCoverage = () => {
    scrollToSection(
      "scenario-current-coverages"
    );
  };

  const handleAdjustStrategy = () => {
    scrollToSection(
      "scenario-candidate-strategy"
    );
  };


  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h2 className="text-lg font-semibold text-slate-900">
          Scenario Risk Financing
        </h2>

        <p className="text-sm text-slate-500 mt-1">
          Analyze retention, transfer and residual exposure
          for an individual FAIR scenario.
        </p>

        <div className="mt-4 max-w-xl mb-4">
          <label className="block">
            <span className="text-xs font-medium text-slate-500">
              Active FAIR scenario
            </span>

            <select
              value={selectedScenarioId}
              onChange={(event) =>
                onScenarioChange(event.target.value)
              }
              disabled={scenariosLoading}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="">
                Select a scenario
              </option>

              {scenarios.map((scenario) => (
                <option
                  key={scenario.id}
                  value={scenario.id}
                >
                  {scenario.name}
                </option>
              ))}
            </select>

          </label>
        </div>

        {scenario && (
            <ScenarioSummaryCard
                scenario={scenario}
                baseline={baseline}
                settings={settings}
            />
        )}

        <div id="scenario-current-coverages"  className="scroll-mt-6 mb-4">
          <CurrentCoveragesPanel
            coverages={currentCoverages}
            settings={settings}
          />
        </div>

        {scenario && (
          <ScenarioFinancialPosition
            scenario={scenario}
            financialPosition={
              financialPosition
            }
            settings={settings}
            onReviewCoverage={
              handleReviewCoverage
            }
            onAdjustStrategy={
              handleAdjustStrategy
            }
            onCompareAlternatives={
              onCompareAlternatives
            }
          />
        )}

        {scenariosError && (
          <p className="text-sm text-red-700 mt-3">
            Unable to load active FAIR scenarios.
          </p>
        )}
      </div>

      <div id="scenario-candidate-strategy" className="scroll-mt-6">
        <InsurancePolicyCard
          strategy={strategy}
          settings={settings}
          onChange={onStrategyChange}
          onRecalculate={onAnalyze}
          loading={analysisLoading}
        />
      </div>

      {analysisError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          Unable to calculate the scenario financing
          analysis.
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4">
            <ScenarioKpi
              title="Baseline EAL"
              value={formatMoney(
                baseline.expected_annual_loss,
                currency
              )}
            />

            <ScenarioKpi
              title="P95"
              value={formatMoney(
                baseline.p95,
                currency
              )}
            />

            <ScenarioKpi
              title="Transferred EAL"
              value={formatMoney(
                summary.transferred_eal,
                currency
              )}
            />

            <ScenarioKpi
              title="Retained EAL"
              value={formatMoney(
                summary.retained_eal,
                currency
              )}
            />

            <ScenarioKpi
              title="Residual Exposure"
              value={formatMoney(
                summary.residual_eal,
                currency
              )}
            />

            <ScenarioKpi
              title="Transfer Efficiency"
              value={formatRatio(
                summary.transfer_efficiency
              )}
            />
          </div>

          <RiskFinancingCurve
            data={data}
            strategy={strategy}
            settings={settings}
          />
        </>
      )}
    </div>
  );
}

function ScenarioKpi({
  title,
  value,
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <p className="text-xs font-medium text-slate-500">
        {title}
      </p>

      <p className="text-xl font-semibold text-slate-900 mt-2">
        {value}
      </p>
    </div>
  );
}